import { passkeyClient } from "@better-auth/passkey/client";
import {
  startAuthentication,
  WebAuthnAbortService,
} from "@simplewebauthn/browser";
import { createAuthClient } from "better-auth/client";
import { t } from "i18next";
import { env } from "@/env";
import { NativeAuthRequestError, SIGN_UP_STARTED_KEY } from "@/lib/native-auth";

const client = createAuthClient({
  baseURL: new URL("/api/auth", env.VITE_API_V2_BASE_URL).href,
  fetchOptions: { credentials: "include", cache: "no-store" },
  plugins: [passkeyClient()],
});

// Button prompts in progress; autofill never opens while one runs.
let prompts = 0;
let waitingAutofill: Promise<unknown> | null = null;

export async function signInWithNativePasskey(): Promise<unknown> {
  prompts++;
  try {
    const autofill = waitingAutofill;
    if (autofill) {
      // End autofill on purpose and prompt only once it has settled, so the
      // two requests never race (an implicit abort can hang Safari).
      // startAuthentication opens its abort controller after its own async
      // check, so keep cancelling until autofill settles.
      WebAuthnAbortService.cancelCeremony();
      const cancel = setInterval(
        () => WebAuthnAbortService.cancelCeremony(),
        50,
      );
      const picked = await autofill.finally(() => clearInterval(cancel));
      if (picked) return picked;
    }
    const result = await client.signIn.passkey();
    if (result.error) throw passkeyRejected(result.error);
    return result.data;
  } finally {
    prompts--;
  }
}

// A deleted passkey can still sit in the browser or password manager.
function passkeyRejected(error: object) {
  const body = error as { code?: string; error?: { code?: string } };
  return Error(
    (body.code ?? body.error?.code) === "PASSKEY_NOT_FOUND"
      ? t("auth.native.errors.passkeyRemoved")
      : t("auth.native.errors.passkeyRejected"),
  );
}

// Resolves null unless the user picks a saved passkey from autofill, and
// rejects when the server refuses the picked passkey. Callers
// share one request: a second one aborts the first, which hangs Safari. The
// passkey button ends it explicitly before prompting.
export function waitForNativePasskeyAutofill(): Promise<unknown> {
  if (prompts) return Promise.resolve(null);
  waitingAutofill ??= requestPasskeyAutofill().finally(() => {
    waitingAutofill = null;
  });
  return waitingAutofill;
}

// Runs the client's sign-in steps itself so the button can stop it between
// fetching options and opening the browser request.
async function requestPasskeyAutofill(): Promise<unknown> {
  let credential: Awaited<ReturnType<typeof startAuthentication>>;
  try {
    if (
      !(await window.PublicKeyCredential?.isConditionalMediationAvailable?.())
    )
      return null;
    const options = await client.$fetch<
      Parameters<typeof startAuthentication>[0]["optionsJSON"]
    >("/passkey/generate-authenticate-options", { method: "GET" });
    if (prompts || !options.data) return null;
    credential = await startAuthentication({
      optionsJSON: options.data,
      useBrowserAutofill: true,
    });
  } catch {
    return null;
  }
  const { clientExtensionResults: _, ...response } = credential;
  const result = await client.$fetch("/passkey/verify-authentication", {
    method: "POST",
    body: { response },
  });
  if (result.error) throw passkeyRejected(result.error);
  return result.data;
}

// Browser failures carry their code directly; server failures nest it under
// `error` (the auth proxy's response body).
export async function addNativePasskey(
  name = passkeyDeviceName(),
): Promise<void> {
  const { error } = await client.passkey.addPasskey({ name });
  if (!error) return;
  const failure = error as typeof error & {
    code?: string;
    error?: { code?: string };
  };
  throw new NativeAuthRequestError(
    failure.message ?? "Unable to add this passkey.",
    failure.status,
    failure.code ?? failure.error?.code ?? "",
  );
}

type NavigatorInfo = {
  userAgent: string;
  userAgentData?: { brands: { brand: string }[]; platform: string };
};

const BROWSERS: [RegExp, string][] = [
  [/Edg/, "Edge"],
  [/Firefox|FxiOS/, "Firefox"],
  [/Chrome|CriOS|Chromium/, "Chrome"],
  [/Safari/, "Safari"],
];
const PLATFORMS: [RegExp, string][] = [
  [/iPhone|iPad|iPod/, "iOS"],
  [/Android/, "Android"],
  [/CrOS|Chrome OS/, "ChromeOS"],
  [/Mac/, "macOS"],
  [/Windows/, "Windows"],
  [/Linux/, "Linux"],
];

export function passkeyDeviceName(
  nav: NavigatorInfo = navigator as NavigatorInfo,
): string {
  const brands = nav.userAgentData?.brands.map((item) => item.brand) ?? [];
  const browserText = [...brands, nav.userAgent].join(" ");
  const platformText = `${nav.userAgentData?.platform ?? ""} ${nav.userAgent}`;
  const browser = BROWSERS.find(([pattern]) => pattern.test(browserText))?.[1];
  const platform = PLATFORMS.find(([pattern]) =>
    pattern.test(platformText),
  )?.[1];
  return browser && platform ? `${browser} on ${platform}` : "Passkey";
}

// The device name, numbered "(1)", "(2)"... when another passkey has it.
export function suggestPasskeyName(
  taken: (string | null | undefined)[],
  base = passkeyDeviceName(),
): string {
  const used = new Set(taken.map((name) => name?.trim().toLowerCase()));
  let name = base;
  for (let count = 1; used.has(name.toLowerCase()); count++)
    name = `${base} (${count})`;
  return name;
}

// Never throws: a cancelled prompt or stale session falls back to Settings.
export async function addNativePasskeyAfterSignUp(): Promise<boolean> {
  try {
    return !(await client.passkey.addPasskey({ name: passkeyDeviceName() }))
      .error;
  } catch {
    return false;
  }
}

// Stays inside the server's 5-minute fresh-session window with headroom.
const SIGN_UP_PASSKEY_WINDOW_MS = 240_000;

export async function canOfferSignUpPasskey(): Promise<boolean> {
  const started = Number(sessionStorage.getItem(SIGN_UP_STARTED_KEY));
  const withinWindow = () => Date.now() - started < SIGN_UP_PASSKEY_WINDOW_MS;
  if (!started || !withinWindow()) return false;
  try {
    return (
      (await window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable?.()) ===
        true && withinWindow()
    );
  } catch {
    return false;
  }
}
